import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { isNative, WEB_ORIGIN, redirectExternal } from "@/lib/native";
import { ArrowLeft, Trash2, Minus, Plus, Store, Info, Package } from "lucide-react";
import { money } from "@/components/marketplace/shared";

export default function Cart() {
  const [cart, setCart] = useState(null);
  const load = () => api.get("/cart").then(r => setCart(r.data)).catch(() => setCart({ groups: [], total_minor: 0, items_count: 0 }));
  useEffect(() => { load(); }, []);

  const patch = async (item_id, body) => {
    try { const { data } = await api.patch(`/cart/items/${item_id}`, body); setCart(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (item_id) => { const { data } = await api.delete(`/cart/items/${item_id}`); setCart(data); };
  const [busy, setBusy] = useState(false);
  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", { origin_url: isNative ? WEB_ORIGIN : window.location.origin });
      const how = await redirectExternal(data.checkout_url);
      if (how === "blocked" || how === "popup") setPayLink(data.checkout_url);
      setTimeout(() => setBusy(false), 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const [payLink, setPayLink] = useState(null);

  if (!cart) return <div className="page pt-8 text-center text-zinc-500">A carregar…</div>;
  return (
    <div className="page fade-up">
      <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Marketplace</Link>
      <h1 className="font-display text-2xl font-extrabold mb-3">Carrinho</h1>
      {cart.groups.length === 0 && (
        <div data-testid="cart-empty" className="text-center py-12 text-zinc-500 text-sm">O teu carrinho está vazio.<br /><Link to="/marketplace" className="text-[#c8102e] font-semibold underline">Explorar produtos</Link></div>
      )}
      <div className="space-y-4 mb-4">
        {cart.groups.map(g => (
          <div key={g.seller_id} data-testid={`cart-group-${g.seller_id}`} className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
            <Link to={`/negocios/${g.seller_id}`} data-testid={`cart-seller-link-${g.seller_id}`} className="flex items-center gap-2 px-4 py-2.5 bg-zinc-50 border-b border-zinc-100 text-sm font-semibold"><Store size={14} /> {g.seller_name}</Link>
            {g.items.map(it => (
              <div key={it.item_id} data-testid={`cart-item-${it.item_id}`} className="flex gap-3 p-3 border-b border-zinc-100 last:border-0">
                <div className="w-16 h-16 rounded-lg bg-zinc-100 overflow-hidden shrink-0">{it.photo && <img src={it.photo} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <Link to={`/marketplace/produto/${it.product_id}`} className="font-semibold text-sm truncate block">{it.name}</Link>
                  {it.variant && <div className="text-[11px] text-zinc-500">{Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>}
                  {it.modes.length > 1 ? (
                    <select value={it.fulfilment_mode} onChange={e => patch(it.item_id, { fulfilment_mode: e.target.value })} className="text-[11px] mt-1 border border-zinc-200 rounded px-1 py-0.5">
                      <option value="delivery">Entrega</option><option value="pickup">Levantamento</option>
                    </select>
                  ) : <div className="text-[11px] text-zinc-500 mt-0.5">{it.fulfilment_mode === "delivery" ? "Entrega" : "Levantamento"}</div>}
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center border border-zinc-200 rounded-lg">
                      <button onClick={() => patch(it.item_id, { qty: it.qty - 1 })} className="p-1.5"><Minus size={12} /></button>
                      <span className="w-6 text-center text-xs font-bold">{it.qty}</span>
                      <button onClick={() => patch(it.item_id, { qty: Math.min(it.stock, it.qty + 1) })} className="p-1.5"><Plus size={12} /></button>
                    </div>
                    <span className="font-bold text-sm">{money(it.line_minor, g.currency)}</span>
                  </div>
                </div>
                <button data-testid={`cart-remove-${it.item_id}`} onClick={() => remove(it.item_id)} className="text-zinc-400 hover:text-[#c8102e] self-start"><Trash2 size={15} /></button>
              </div>
            ))}
            <div className="px-4 py-2.5 text-xs text-zinc-600 flex justify-between bg-zinc-50">
              <span>Subtotal {money(g.subtotal_minor, g.currency)}{g.delivery_minor ? ` + entrega ${money(g.delivery_minor, g.currency)}` : ""}</span>
              <span className="font-bold text-black">{money(g.total_minor, g.currency)}</span>
            </div>
          </div>
        ))}
      </div>
      {cart.groups.length > 0 && (
        <div className="bg-black text-white rounded-2xl p-4 mb-8">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm">Total ({cart.groups.length} {cart.groups.length === 1 ? "vendedor" : "vendedores"})</span>
            <span data-testid="cart-total" className="font-display text-2xl font-extrabold">{money(cart.total_minor, cart.currency)}</span>
          </div>
          <p className="text-[11px] text-white/60 mb-3 flex items-start gap-1"><Info size={12} className="shrink-0 mt-0.5" /> Será criada uma encomenda separada por vendedor. Cada vendedor só vê a sua parte.{cart.checkout_mode === "delivery" && " A morada de entrega (Reino Unido) é pedida no pagamento."}</p>
          {cart.mixed_modes && <p data-testid="cart-mixed-warning" className="text-[11px] text-black bg-[#ffd100] rounded-lg p-2 mb-3 font-semibold">Não é possível misturar entrega e levantamento no mesmo checkout. Escolhe um único método para todos os artigos ou separa a compra.</p>}
          <button data-testid="cart-checkout" disabled={busy || cart.mixed_modes} onClick={checkout} className="w-full py-3 rounded-xl bg-[#ffd100] hover:bg-[#e6bd00] disabled:opacity-60 text-black font-bold text-sm flex items-center justify-center gap-2">
            <Package size={16} /> {busy ? "A preparar pagamento…" : "Pagar com Stripe"}
          </button>
          {cart.stripe_mode === "test" && <p data-testid="cart-test-hint" className="text-[10px] text-white/50 mt-2 text-center">Ambiente de teste — cartão 4242 4242 4242 4242 · qualquer data futura · qualquer CVC</p>}
          {payLink && (
            <a data-testid="cart-pay-link" href={payLink} target="_blank" rel="noopener noreferrer"
               className="mt-2 block text-center text-xs font-semibold underline text-[#ffd100]">
              O pagamento abriu numa nova janela. Se não apareceu, toca aqui para abrir o Stripe.
            </a>
          )}
        </div>
      )}
    </div>
  );
}
